import {
  RoleCode,
  RoleFaction,
  type ParsedGameRoster,
  type PlayerRoleType,
} from '@repo/sdk'

import { formatGameSettingDisplay } from '@/lib/play'
import type { TournamentEdition } from './edition'

type Translate = (
  key: string,
  params?: Record<string, string | number>,
) => string

const legacySummaryLines: Record<string, string[]> = {
  en: [
    '# free Games exclusively for this event',
    '# god-role Games + # Mechanical Wolf Games, in any order',
    'God roles: Psychic, Guard, Hunter, Witch',
    'The four god roles share a #-Game allowance',
  ],
  zh: [
    '活动专属免费 # 局',
    '# 局神职 + # 局机械狼，可以交替',
    '神职可选：通灵师、守卫、猎人、女巫',
    '四种神职共用 # 局额度',
  ],
  tw: [
    '活動專屬免費 # 局',
    '# 局神職 + # 局機械狼，可以交替',
    '神職可選：通靈師、守衛、獵人、女巫',
    '四種神職共用 # 局額度',
  ],
  es: [
    '# partidas gratis exclusivas para este evento',
    '# partidas con roles divinos + # partidas como Lobo mecánico, en cualquier orden',
    'Roles divinos: Médium, Guardia, Cazador, Bruja',
    'Los cuatro roles divinos comparten una cuota de # partidas',
  ],
  fr: [
    '# parties gratuites exclusivement pour cet événement',
    '# parties avec des rôles divins + # parties en tant que Loup mécanique, dans n’importe quel ordre',
    'Rôles divins : Médium, Garde, Chasseur, Sorcière',
    'Les quatre rôles divins partagent un quota de # parties',
  ],
  ar: [
    '# مباريات مجانية حصرية لهذه الفعالية',
    '# مباريات بالأدوار الإلهية + # مباريات بدور الذئب الآلي، بأي ترتيب',
    'الأدوار الإلهية: الوسيط الروحي، الحارس، الصياد، الساحرة',
    'تتشارك الأدوار الإلهية الأربعة حصة من # مباريات',
  ],
  ja: [
    'このイベント限定で#ゲーム無料',
    '神職で#ゲーム + 機械狼で#ゲーム、順番は自由',
    '選択可能な神職：霊媒師、守護者、狩人、魔女',
    '#種類の神職で#ゲームの枠を共有',
  ],
  ko: [
    '이벤트 전용 무료 게임 #회',
    '신직 역할 게임 #회 + 기계 늑대 게임 #회, 순서는 자유',
    '선택 가능한 신직 역할: 영매, 수호자, 사냥꾼, 마녀',
    '네 가지 신직 역할이 게임 #회 이용 횟수를 공유합니다',
  ],
}

export function tournamentSummary(
  edition: TournamentEdition,
  language: string,
  t: Translate,
) {
  const { configuration } = edition
  const godCategory = configuration.categories.find(
    category => !category.allowedRoles.includes('mechanical_wolf'),
  )
  const wolfCategory = configuration.categories.find(category =>
    category.allowedRoles.includes('mechanical_wolf'),
  )
  const counts = new Map<PlayerRoleType, number>()
  for (const role of configuration.roster)
    counts.set(role, (counts.get(role) ?? 0) + 1)
  const roleCodes = new Map(Object.entries(RoleCode))
  const factions = new Map(Object.entries(RoleFaction))
  const roles = [...counts].map(([roleKey, count]) => ({
    roleKey,
    count,
    code: roleCodes.get(roleKey) ?? roleKey,
    faction:
      factions.get(roleKey) === 'evil' ? ('evil' as const) : ('good' as const),
  }))
  const roster: ParsedGameRoster = {
    playerCount: configuration.roster.length,
    gameType: 'G',
    isRandom: false,
    goodRoles: roles.filter(role => role.faction === 'good'),
    evilRoles: roles.filter(role => role.faction === 'evil'),
  }
  const separator = ['zh', 'tw', 'ja'].includes(language) ? '、' : ', '
  const rosterDisplay = formatGameSettingDisplay(roster, t)
    .replaceAll(' | ', ', ')
    .replaceAll(', ', separator)
  const setup = t('tournamentSummary.gameSetup', {
    roster: rosterDisplay,
    sheriff: configuration.hasSheriff
      ? t('tournamentSummary.sheriffEnabled')
      : '',
  })
  if (!godCategory || !wolfCategory || configuration.categories.length !== 2) {
    return [
      ...edition.content.summary,
      setup,
      t('tournamentRules.selectionPending'),
    ]
  }
  const godRoles = godCategory.allowedRoles.map(role => t(`game.${role}`))
  const choices
    = language === 'en' && godRoles.length > 1
      ? `${godRoles.slice(0, -1).join(', ')} or ${godRoles.at(-1)}`
      : godRoles.join(separator)
  return [
    t('tournamentSummary.freeGames', {
      total: configuration.categories.reduce(
        (sum, category) => sum + category.limit,
        0,
      ),
      godLimit: godCategory.limit,
      wolfLimit: wolfCategory.limit,
    }),
    t('tournamentSummary.godRoles', {
      roles: choices,
      roleCount:
        language === 'en' && godRoles.length === 4 ? 'four' : godRoles.length,
      limit: godCategory.limit,
    }),
    setup,
    ...edition.content.summary.filter(
      line =>
        !(legacySummaryLines[language] ?? []).includes(
          line.replaceAll(/\d+/g, '#'),
        ),
    ),
    t('tournamentRules.selectionPending'),
  ]
}
